import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { BsWhatsapp } from 'react-icons/bs';
import { useParams } from 'react-router-dom';
import { IMessage } from '../../commons/interfaces/chat.interface';
import { IChatbotAPIPagination } from '../../commons/interfaces/chatbotAPI.interface';
import { ITicket } from '../../commons/interfaces/ticket.interface';
import { SocketContext } from '../../contexts/socket.context';
import ChatbotAPI from '../../services/chatbot.service';
import ChatFooter from './ChatFooter';
import MessageList from './MessageList';

const chatbotAPI = new ChatbotAPI();

interface IChatBodyProps {
  ticket: ITicket;
}

const ChatBody = ({ ticket }: IChatBodyProps) => {
  const params = useParams();
  const socket = useContext(SocketContext);
  const bottomChatBodyRef = useRef<HTMLDivElement>(null);

  const initialPagination = { page: 1, pageSize: 20, pageCount: 0, totalCount: 0 };
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [pagination, setPagination] = useState<IChatbotAPIPagination>(initialPagination);

  const scrollToBotton = () => {
    bottomChatBodyRef.current?.scrollIntoView();
  };

  // fetching messages
  useEffect(() => {
    const fetchData = async () => {
      const response = await chatbotAPI.listTicketMessages(params.ticketId as string, 1);

      setMessages(response.data);
      setPagination(response.pagination);
      setTimeout(() => scrollToBotton(), 100);
    };

    fetchData();
  }, [params.ticketId]);

  const addMessage = useCallback(
    (newMessage: IMessage) => {
      setMessages([newMessage, ...messages]);
    },
    [messages]
  );

  // Add socket listeners
  useEffect(() => {
    socket.emit('subscribe', params.ticketId);
    socket.on('message:create', addMessage);

    return () => {
      socket.emit('unsubscribe', params.ticketId);
      socket.off('message:create');
    };
  }, [socket, params, addMessage]);

  const addOldMessages = (oldMessages: IMessage[]) => {
    setMessages([...messages, ...oldMessages]);
  };

  const sendMessage = async (currentMessage: string) => {
    const body = { message: currentMessage, sendType: 'MANAGER' };
    const response = await chatbotAPI.createTicketMessage(params.ticketId as string, body);
    addMessage(response.data);
    scrollToBotton();
  };

  return (
    <div className="chat">
      <Row className="bg-green chat-header">
        <Col className="chat-header-info">
          <Stack>
            <span className="header-info-name">{ticket.customer.name}</span>
            <span>{ticket.customer.email}</span>
          </Stack>
        </Col>
        <Col xs="auto" className="chat-header-icon">
          <BsWhatsapp />
        </Col>
      </Row>
      <MessageList
        messages={messages}
        addOldMessages={addOldMessages}
        pagination={pagination}
        setPagination={setPagination}
        bottomRef={bottomChatBodyRef}
      />
      <ChatFooter ticket={ticket} sendMessage={sendMessage} />
    </div>
  );
};

export default ChatBody;
